import { useTranslation } from 'react-i18next'
import type { SelectedPoint } from '@/components/map/MapCanvas'
import NumberField from '@/components/common/NumberField'
import FloatingPanel from './FloatingPanel'

interface PropertiesPanelProps {
  point: SelectedPoint
  pos: { x: number; y: number }
  onMove: (pos: { x: number; y: number }) => void
  onClose: () => void
  /**
   * 向きの変更（記録ルートの端点スロットのみ）。指定時は向きが編集可能になり、
   * 変更は編集セッションへ積まれる（隣のマークが端点を中心に回転する）。
   */
  onRotate?: (thetaRad: number) => void
}

// 選択した点の座標・向きを表示するフローティングパネル。
export default function PropertiesPanel({
  point,
  pos,
  onMove,
  onClose,
  onRotate,
}: PropertiesPanelProps) {
  const { t } = useTranslation()

  const label = point.id.split('/').pop() ?? point.id
  const thetaDeg = (((point.theta * 180) / Math.PI + 540) % 360) - 180

  return (
    <FloatingPanel
      title={t('scenarios.properties')}
      pos={pos}
      onMove={onMove}
      onClose={onClose}
    >
      {/* プロパティ行 */}
      <div className="p-2.5 text-xs space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-gray-500">{t('scenarios.slotKind')}</span>
          <span
            className={`px-1.5 py-0.5 rounded text-[10px] text-white ${
              point.kind === 'slot' ? 'bg-pink-500' : 'bg-blue-500'
            }`}
          >
            {point.kind === 'slot'
              ? t('scenarios.slotKind')
              : t('scenarios.pointKind')}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-gray-500">{t('scenarios.id')}</span>
          <span className="font-medium text-gray-800 truncate" title={point.id}>
            {label}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">x</span>
          <span className="tabular-nums text-gray-800">
            {point.x.toFixed(3)} m
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">y</span>
          <span className="tabular-nums text-gray-800">
            {point.y.toFixed(3)} m
          </span>
        </div>
        {onRotate ? (
          <div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-gray-500">
                {t('scenarios.orientation')}
              </span>
              <div className="flex items-center gap-1">
                <NumberField
                  value={Number(thetaDeg.toFixed(1))}
                  step={1}
                  onCommit={(v) => onRotate((v * Math.PI) / 180)}
                />
                <span className="text-gray-500">°</span>
              </div>
            </div>
            <div className="mt-0.5 text-[10px] text-gray-400">
              {t('scenarios.propertiesRotateHint')}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{t('scenarios.orientation')}</span>
            <span className="tabular-nums text-gray-800">
              {thetaDeg.toFixed(1)}°
            </span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-gray-500">rad</span>
          <span className="tabular-nums text-gray-800">
            {point.theta.toFixed(3)}
          </span>
        </div>
        {point.isCargo && point.height != null && (
          <div className="flex items-center justify-between border-t pt-1.5">
            <span className="flex items-center gap-1 text-gray-500">
              <span className="px-1 py-0.5 text-[10px] font-bold text-white bg-orange-500 rounded leading-none">
                高
              </span>
              {t('scenarios.forkHeight')}
            </span>
            <span className="tabular-nums font-medium text-orange-600">
              {Math.floor(point.height * 1000).toLocaleString()} mm
            </span>
          </div>
        )}
      </div>
    </FloatingPanel>
  )
}
